import {
  AccountBalance,
  Assignment,
  AssignmentInd,
  AttachMoney,
  BusinessCenter,
  Description,
  MoreHoriz,
  OpenInNew,
  Person,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { SGR_STATES } from 'lib/consts';
import InfoCard from 'lib/designSystem/InfoCard';
import { formatCurrency } from 'lib/functions/utils';
import React from 'react';

type Menu = {
  onOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseMenu: () => void;
  menuAnchorEl: HTMLElement | null;
  menuOpen: boolean;
  menuOptions: { label: string; action: () => void }[];
};

interface GeneralInfoProps {
  warrantyStatus: (typeof SGR_STATES)[number] | undefined;
  warrantyType: string;
  quotedAmount?: number | null;
  quotedCurrency?: 'CLP' | 'USD' | 'UF' | null;
  tenderId?: string | number | null;
  description?: string | null;
  business?: { primary?: string; secondary?: string };
  tenderUrl?: string;
  fogape?: number | null;
  menu?: Menu;
  pruffConnect?: boolean;
}

const GeneralInfo: React.FC<GeneralInfoProps> = ({
  warrantyStatus,
  warrantyType,
  quotedAmount,
  quotedCurrency,
  tenderId,
  description,
  business,
  tenderUrl,
  fogape,
  menu,
  pruffConnect,
}) => {
  return (
    <Paper
      sx={{
        p: 1.5,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        mb: 2,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.1,
          }}
        >
          Información General
        </Typography>
        <Chip
          label={warrantyStatus?.label}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: '18px', fontSize: '0.65rem' }}
        />
        {menu && (
          <>
            <IconButton
              aria-label="Opciones"
              size="small"
              sx={{ position: 'absolute', right: 8, top: 8 }}
              onClick={menu.onOpenMenu}
            >
              <MoreHoriz />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={menu.menuAnchorEl}
              open={menu.menuOpen}
              onClose={menu.onCloseMenu}
              MenuListProps={{ 'aria-labelledby': 'long-button' }}
            >
              {menu.menuOptions.map((opt) => (
                <MenuItem
                  key={opt.label}
                  onClick={() => {
                    menu.onCloseMenu();
                    opt.action();
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<BusinessCenter />}
            label="Tipo de Garantía"
            value={warrantyType}
            color="blue"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<AttachMoney />}
            label="Monto Cotizado"
            value={formatCurrency(quotedAmount ?? 0, quotedCurrency)}
            color="green"
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
          {tenderUrl && (
            <IconButton
              component="a"
              href={tenderUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Abrir licitación"
              sx={{ position: 'absolute', left: '51%', top: 20 }}
            >
              <OpenInNew sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          )}
          <InfoCard
            icon={<Assignment />}
            label="Número de Licitación"
            value={tenderId ? `#${tenderId}` : 'N/A'}
            color="orange"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<AccountBalance />}
            label="Fogape"
            value={fogape ? `Si / ${fogape}%` : 'No / -'}
            color="orange"
          />
        </Grid>
        {!pruffConnect && (
          <>
            <Grid item xs={12} md={6}>
              <InfoCard
                icon={<Person />}
                label="Afianzado"
                value={business?.primary ?? 'N/A'}
                color="blue"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoCard
                icon={<AssignmentInd />}
                label="RUT Afianzado"
                value={business?.secondary ?? 'N/A'}
                color="blue"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <InfoCard
            icon={<Description />}
            label="Glosa"
            value={description ?? 'N/A'}
            color="brown"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default GeneralInfo;
